// DeliverPrint_ProductTab.js
// For now this is a 1:1 clone of the current ProductTab implementation.
// We will later customize the UI to match DeliverPrint style.

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, getLanguageFromUrl } from '../../utils/languageUtils';
import { getOptionDisplayName } from '../../utils/productOptionsUtils';
import { getGlobalSelectedOptions, setGlobalSelectedOptions } from '../../utils/globalOptions';
import './ProductTab.css';
import './DeliverPrint_ProductTab.css';
import OptionNoteModal from '../ui/OptionNoteModal';

const DeliverPrint_ProductTab = ({ product, onProductSelect, konvaController, configData }) => {
  const { t } = useTranslation();
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [batchQty, setBatchQty] = useState({});
  // Build an ordered list of available options (Option1..OptionN)
  const optionsList = useMemo(() => {
    const opts = configData?.SupportedModels?.Options || {};
    // keep keys sorted by the trailing number if present (Option1, Option2, ...)
    const entries = Object.entries(opts)
      .filter(([, def]) => def && Array.isArray(def.values) && def.values.length > 0)
      .sort(([a], [b]) => {
        const na = parseInt(String(a).replace(/[^0-9]/g, ''), 10) || 0;
        const nb = parseInt(String(b).replace(/[^0-9]/g, ''), 10) || 0;
        return na - nb;
      });
    return entries; // [ ["Option1", def], ["Option2", def], ... ]
  }, [configData]);
  
  // Use state object to track selected option raw values keyed by OptionX
  const [selectedOptions, setSelectedOptions] = useState({});

  // Helper function to get display name in current language
  const getDisplayName = (displayName) => {
    if (!displayName) return '';
    if (typeof displayName === 'string') {
      return displayName; // Backward compatibility
    }
    if (typeof displayName === 'object') {
      const currentLang = getLanguageFromUrl();
      return displayName[currentLang] || displayName['en'] || displayName['cn'] || '';
    }
    return '';
  };

  // Track the previous product ID to avoid resetting options when only the view changes
  const [prevProductId, setPrevProductId] = useState('');
  
  // Set initial options when component mounts or product changes (but not when only view changes)
  useEffect(() => {
    if (!product) return;
    
    // Get the current product ID
    const currentProductId = product.ProductionName || product.id || '';
    
    // Only update product selection if it's a new product
    if (onProductSelect && prevProductId !== currentProductId) {
      onProductSelect(product);
      setPrevProductId(currentProductId);
    }
    
    // Check if konvaController already has options set
    const currentOptions = konvaController?.getCurrentOptions?.() || {};
    // Also check global options
    const globalOptions = getGlobalSelectedOptions();
    
    // Only initialize options if:
    // 1. This is a new product (different ID), or
    // 2. We don't have options set yet
    if (prevProductId !== currentProductId || Object.keys(selectedOptions).length === 0) {
      // Initialize options from controller/global/product/config defaults for ALL defined options
      const newOptions = { ...selectedOptions };
      for (const [optKey, def] of optionsList) {
        // Priority: controller > global > product defaults (legacy) > first config value
        if (currentOptions?.[optKey]?.value !== undefined) {
          newOptions[optKey] = currentOptions[optKey].value;
        } else if (typeof currentOptions?.[optKey] === 'string') {
          newOptions[optKey] = currentOptions[optKey];
        } else if (globalOptions?.[optKey]?.value !== undefined) {
          newOptions[optKey] = globalOptions[optKey].value;
        } else if (typeof globalOptions?.[optKey] === 'string') {
          newOptions[optKey] = globalOptions[optKey];
        } else if (product?.Options?.[optKey]) {
          newOptions[optKey] = product.Options[optKey];
        } else if (Array.isArray(def?.values) && def.values.length > 0) {
          // Find the first non-disabled option
          const firstEnabledOption = def.values.find(opt => opt.disabled !== true);
          if (firstEnabledOption) {
            newOptions[optKey] = firstEnabledOption.value;
          }
        }
      }
      setSelectedOptions(newOptions);
    }
    
    // Redraw the canvas when product changes
    if (konvaController?.redraw && typeof konvaController.redraw === 'function') {
      konvaController.redraw();
    }
  
  }, [product, onProductSelect, konvaController, configData, selectedOptions, prevProductId]);

  // Update product with explicitly provided options (avoids React state timing issues)
  const updateProductWithOptionsDirectly = (options) => {
    if (!product || !konvaController) return;

    // Determine selected view: preserve current if possible, otherwise first available
    const currentView = konvaController.selectedView;
    const availableViews = product.Views || product.views || [];
    const getViewId = (v) => {
      if (!v) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'object' && v.id) return v.id;
      return String(v);
    };
    let defaultView = null;
    if (currentView) {
      const currId = getViewId(currentView);
      const match = availableViews.find(v => getViewId(v) === currId);
      defaultView = match || (availableViews.length > 0 ? availableViews[0] : null);
    } else {
      defaultView = availableViews.length > 0 ? availableViews[0] : null;
    }

    const productId = product.ProductionName || 'default-product';
    const baseUrl = (configData && configData.BaseProductUrl) || product.BaseProductUrl || '';
    if (productId && baseUrl) {
      const optionsObject = {};
      for (const [optKey] of optionsList) {
        const raw = options?.[optKey];
        if (raw !== undefined && raw !== null && raw !== '') {
          optionsObject[optKey] = { value: raw };
        }
      }
      setGlobalSelectedOptions(optionsObject);
      konvaController.loadProduct(
        productId,
        baseUrl,
        product.hasDesign || false,
        product.DesignUrl || null,
        defaultView,
        product.CaseUrl || configData?.CaseUrl || null
      );
      if (konvaController?.redraw && typeof konvaController.redraw === 'function') {
        konvaController.redraw();
      }
    }
  };

  useEffect(() => {
    try {
      const existing = getGlobalSelectedOptions() || {};
      const next = { ...existing, BatchQtyMatrix: { value: batchQty } };
      setGlobalSelectedOptions(next);
      if (typeof window !== 'undefined') window.BatchQtyMatrix = batchQty;
    } catch {}
  }, [batchQty]);

  const optionsDefs = configData?.SupportedModels?.Options || {};
  const batchEnabled = !!(configData && configData.BatchEnable);
  const findKeyByName = (n) => {
    const nn = String(n || '').toLowerCase();
    for (const [k, def] of Object.entries(optionsDefs)) {
      const nm = String(def?.name || '').toLowerCase();
      if (nm === nn) return k;
      const dn = typeof def?.displayName === 'string' ? def.displayName : (def?.displayName?.en || def?.displayName?.cn || '');
      if (String(dn).toLowerCase() === nn) return k;
    }
    return null;
  };
  const colorKey = findKeyByName('color') || Object.keys(optionsDefs).find(k => {
    const d = optionsDefs[k];
    const method = String(d?.displayMethod || '').toLowerCase();
    return d?.isVisualOption === true && method.includes('colorswatch');
  }) || null;
  const sizeKey = findKeyByName('size') || null;
  const colorsDef = colorKey ? optionsDefs[colorKey] : null;
  const sizesDef = sizeKey ? optionsDefs[sizeKey] : null;
  const colors = Array.isArray(colorsDef?.values) ? colorsDef.values : [];
  const sizes = Array.isArray(sizesDef?.values) ? sizesDef.values : [];

  const quickAddItem = () => {
    let cVal = colorKey ? selectedOptions?.[colorKey] : null;
    let sVal = sizeKey ? selectedOptions?.[sizeKey] : null;
    if (!cVal && colors.length) {
      const firstEnabled = colors.find(opt => opt.disabled !== true);
      cVal = firstEnabled ? firstEnabled.value : colors[0]?.value;
    }
    if (sizeKey && !sVal && sizes.length) {
      const firstEnabled = sizes.find(opt => opt.disabled !== true);
      sVal = firstEnabled ? firstEnabled.value : sizes[0]?.value;
    }
    if (!cVal) return;
    const k = `${cVal}|${sizeKey ? (sVal || sizes[0]?.value || 'SIZE_NA') : 'SIZE_NA'}`;
    setBatchQty(m => ({ ...m, [k]: (m[k] ?? 0) + 1 }));
  };
  const removeEntry = (k) => setBatchQty(m => {
    const n = { ...m };
    delete n[k];
    return n;
  });
  const incEntry = (k) => setBatchQty(m => ({ ...m, [k]: (m[k] ?? 0) + 1 }));
  const decEntry = (k) => setBatchQty(m => {
    const q = Math.max(0, (m[k] ?? 0) - 1);
    if (q === 0) {
      const n = { ...m };
      delete n[k];
      return n;
    }
    return { ...m, [k]: q };
  });
  const goPreview = (colorVal, sizeVal) => {
    const next = { ...selectedOptions };
    if (colorKey) next[colorKey] = colorVal;
    if (sizeKey && sizeVal !== 'SIZE_NA') next[sizeKey] = sizeVal;
    setSelectedOptions(next);
    updateProductWithOptionsDirectly(next);
  };

  // Generic option selection handler
  const handleOptionSelect = (optionKey, value) => {
    const newOptions = {
      ...selectedOptions,
      [optionKey]: value
    };
    setSelectedOptions(newOptions);
    updateProductWithOptionsDirectly(newOptions);
  };

  // Helpers for color swatches
  const isCssColor = (val) => {
    if (!val || typeof val !== 'string') return false;
    const s = new Option().style;
    s.color = '';
    s.color = val;
    return s.color !== '';
  };
  const isHex = (val) => typeof val === 'string' && /^#?[0-9a-fA-F]{3,8}$/.test(val);
  const getColorFromOption = (option) => {
    // 1) Prefer explicit swatchHex if present
    const sw = option?.swatchHex || option?.swatchhex;
    if (sw) {
      const s = String(sw).trim();
      if (isHex(s)) return s.startsWith('#') ? s : `#${s}`;
    }

    // 2) Fallback to previous behavior
    const candidate = option?.color || option?.hex || option?.Hex || option?.Color || option?.value || option?.name;
    if (!candidate) return null;
    const c = String(candidate).trim();
    if (isHex(c)) return c.startsWith('#') ? c : `#${c}`;
    if (isCssColor(c)) return c;
    return null;
  };

  // Renderers by displayMethod
  const renderHorizontalButtonGroup = (optKey, def) => {
    const getVariantConstrainedValues = (optKeyLocal, defLocal) => {
      const vals = Array.isArray(defLocal?.values) ? defLocal.values : [];
      const variants = Array.isArray(configData?.variants) ? configData.variants : [];
      if (!variants.length) return vals;
      const constrained = vals.filter((opt) => {
        const candidate = String(opt?.value ?? '').toLowerCase();
        if (!candidate) return false;
        return variants.some((v) => {
          // Candidate must match variant current option key
          const candVal = v?.[optKeyLocal];
          if (candVal == null) return false;
          if (String(candVal).toLowerCase() !== candidate) return false;
          // All other selected options must not contradict the variant
          for (const [k] of optionsList) {
            if (k === optKeyLocal) continue;
            const sel = selectedOptions?.[k];
            if (sel == null || sel === '') continue;
            const vv = v?.[k];
            if (vv != null && String(vv).toLowerCase() !== String(sel).toLowerCase()) return false;
          }
          return true;
        });
      });
      return constrained.length ? constrained : vals;
    };
    const values = getVariantConstrainedValues(optKey, def);
    const count = values.length;
    return (
      <div className="dp-h-button-group" data-opt-key={optKey} style={{ ['--dp-btn-count']: count }}>
        {values.map((option) => {
          const selected = selectedOptions[optKey] === option.value;
          const isDisabled = option.disabled === true;
          return (
            <button
              key={`${optKey}:${option.value}`}
              className={`dp-h-btn ${selected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => !isDisabled && handleOptionSelect(optKey, option.value)}
              disabled={isDisabled}
            >
              {getOptionDisplayName(option.displayName)}
            </button>
          );
        })}
      </div>
    );
  };

  const renderVerticalRadioButton = (optKey, def) => {
    const getVariantConstrainedValues = (optKeyLocal, defLocal) => {
      const vals = Array.isArray(defLocal?.values) ? defLocal.values : [];
      const variants = Array.isArray(configData?.variants) ? configData.variants : [];
      if (!variants.length) return vals;
      const constrained = vals.filter((opt) => {
        const candidate = String(opt?.value ?? '').toLowerCase();
        if (!candidate) return false;
        return variants.some((v) => {
          const candVal = v?.[optKeyLocal];
          if (candVal == null) return false;
          if (String(candVal).toLowerCase() !== candidate) return false;
          for (const [k] of optionsList) {
            if (k === optKeyLocal) continue;
            const sel = selectedOptions?.[k];
            if (sel == null || sel === '') continue;
            const vv = v?.[k];
            if (vv != null && String(vv).toLowerCase() !== String(sel).toLowerCase()) return false;
          }
          return true;
        });
      });
      return constrained.length ? constrained : vals;
    };
    const values = getVariantConstrainedValues(optKey, def);
    return (
      <div className="dp-v-radio-list" data-opt-key={optKey}>
        {values.map((option) => {
          const selected = selectedOptions[optKey] === option.value;
          return (
            <button
              key={`${optKey}:${option.value}`}
              className={`dp-radio-item ${selected ? 'selected' : ''}`}
              onClick={() => handleOptionSelect(optKey, option.value)}
            >
              <span className="dp-radio-icon" aria-hidden="true" />
              <span className="dp-radio-label">{getOptionDisplayName(option.displayName)}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderColorSwatchGrid = (optKey, def) => {
    const getVariantConstrainedValues = (optKeyLocal, defLocal) => {
      const vals = Array.isArray(defLocal?.values) ? defLocal.values : [];
      const variants = Array.isArray(configData?.variants) ? configData.variants : [];
      if (!variants.length) return vals;
      const constrained = vals.filter((opt) => {
        const candidate = String(opt?.value ?? '').toLowerCase();
        if (!candidate) return false;
        return variants.some((v) => {
          const candVal = v?.[optKeyLocal];
          if (candVal == null) return false;
          if (String(candVal).toLowerCase() !== candidate) return false;
          for (const [k] of optionsList) {
            if (k === optKeyLocal) continue;
            const sel = selectedOptions?.[k];
            if (sel == null || sel === '') continue;
            const vv = v?.[k];
            if (vv != null && String(vv).toLowerCase() !== String(sel).toLowerCase()) return false;
          }
          return true;
        });
      });
      return constrained.length ? constrained : vals;
    };
    const values = getVariantConstrainedValues(optKey, def);
    return (
      <div className="dp-color-grid" data-opt-key={optKey}>
        {values.map((option) => {
          const selected = selectedOptions[optKey] === option.value;
          const bg = getColorFromOption(option) || '#ddd';
          return (
            <button
              key={`${optKey}:${option.value}`}
              className={`dp-color-swatch ${selected ? 'selected' : ''}`}
              style={{ background: bg }}
              title={getOptionDisplayName(option.displayName)}
              onClick={() => handleOptionSelect(optKey, option.value)}
            />
          );
        })}
      </div>
    );
  };

  const [qtyByColor, setQtyByColor] = useState({});
  useEffect(() => {
    const def = (configData?.SupportedModels?.Options || {})['Option1'];
    const values = Array.isArray(def?.values) ? def.values : [];
    const init = {};
    values.forEach(v => { init[v.value] = init[v.value] ?? 0; });
    setQtyByColor(prev => ({ ...init, ...prev }));
  }, [configData]);

  useEffect(() => {
    try {
      const existing = getGlobalSelectedOptions() || {};
      const next = { ...existing, ColorQtyMap: { value: qtyByColor } };
      setGlobalSelectedOptions(next);
    } catch {}
  }, [qtyByColor]);

  const renderColorSwatchGridQty = (optKey, def) => {
    const getVariantConstrainedValues = (optKeyLocal, defLocal) => {
      const vals = Array.isArray(defLocal?.values) ? defLocal.values : [];
      const variants = Array.isArray(configData?.variants) ? configData.variants : [];
      if (!variants.length) return vals;
      const constrained = vals.filter((opt) => {
        const candidate = String(opt?.value ?? '').toLowerCase();
        if (!candidate) return false;
        return variants.some((v) => {
          const candVal = v?.[optKeyLocal];
          if (candVal == null) return false;
          if (String(candVal).toLowerCase() !== candidate) return false;
          for (const [k] of optionsList) {
            if (k === optKeyLocal) continue;
            const sel = selectedOptions?.[k];
            if (sel == null || sel === '') continue;
            const vv = v?.[k];
            if (vv != null && String(vv).toLowerCase() !== String(sel).toLowerCase()) return false;
          }
          return true;
        });
      });
      return constrained.length ? constrained : vals;
    };
    const values = getVariantConstrainedValues(optKey, def);
    const totalCfg = def?.qty || def?.total_qty || {};
    const step = totalCfg?.step ?? 1;
    const min = totalCfg?.min ?? 0;
    const perMax = totalCfg?.max ?? 9999;
    const requiredSum = (totalCfg?.sum != null) ? Number(totalCfg.sum) : (totalCfg?.max != null ? Number(totalCfg.max) : null);
    const total = Object.values(qtyByColor || {}).reduce((s, n) => s + (Number(n) || 0), 0);
    return (
      <div data-opt-key={optKey}>
        {values.map((option) => {
          const bg = getColorFromOption(option) || '#ddd';
          const key = option.value;
          const q = qtyByColor[key] ?? 0;
          const inc = () => setQtyByColor(m => {
            const currTotal = Object.values(m).reduce((s, n) => s + (Number(n) || 0), 0);
            if (requiredSum != null && currTotal + step > requiredSum) return m;
            return ({ ...m, [key]: Math.min(perMax, (m[key] ?? 0) + step) });
          });
          const dec = () => setQtyByColor(m => ({ ...m, [key]: Math.max(min, (m[key] ?? 0) - step) }));
          const set = (val) => {
            const n = Math.max(min, Math.min(perMax, parseInt(val || '0', 10) || 0));
            if (requiredSum != null) {
              const others = Object.entries(qtyByColor).filter(([k]) => k !== key).reduce((s, [, v]) => s + (Number(v) || 0), 0);
              if (others + n > requiredSum) return;
            }
            setQtyByColor(m => ({ ...m, [key]: n }));
          };
          return (
            <div
              key={`${optKey}:${key}`}
              className="dp-qty-row"
              onClick={() => handleOptionSelect(optKey, key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOptionSelect(optKey, key);
                }
              }}
            >
              <button
                className="dp-color-swatch"
                style={{ background: bg }}
                title={getOptionDisplayName(option.displayName)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionSelect(optKey, key);
                }}
              />
              <span style={{ color: 'var(--site-text)', fontWeight: 600 }}>{getOptionDisplayName(option.displayName)}</span>
              <div className="dp-qty-controls">
                <button className="dp-qty-btn" onClick={(e) => { e.stopPropagation(); dec(); }}>−</button>
                <input
                  className="dp-qty-input"
                  type="number"
                  min={min}
                  max={perMax}
                  step={step}
                  value={q}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => set(e.target.value)}
                />
                <button className="dp-qty-btn" onClick={(e) => { e.stopPropagation(); inc(); }}>+</button>
              </div>
            </div>
          );
        })}
        
      </div>
    );
  };

  const renderTempCart = () => {
    const entries = Object.entries(batchQty || {})
      .filter(([, q]) => Number(q) > 0);
    const total = entries.reduce((s, [, q]) => s + Number(q || 0), 0);
    return (
      <div className="dp-section" key="TempCart">
        <div className="dp-section-title-row">
          <p className="option-title"><strong>{t('batch.title', 'Batch Order')}</strong></p>
          <span className="dp-qty-summary" style={{ color: '#2e7d32' }}>{total}</span>
        </div>
        <div className="dp-temp-cart">
          <button type="button" className="dp-qty-btn" onClick={quickAddItem}>+ {t('batch.addItem', 'Add Item')}</button>
          {entries.length === 0 ? (
            <p style={{ color: 'var(--site-sub)' }}>{t('batch.noItems', 'No items yet')}</p>
          ) : entries.map(([k, q]) => {
            const [cVal, sVal] = String(k).split('|');
            const cMeta = colors.find(v => String(v.value) === cVal);
            const sMeta = sizeKey ? sizes.find(v => String(v.value) === sVal) : null;
            const bg = getColorFromOption(cMeta) || '#ddd';
            const cName = cMeta ? getOptionDisplayName(cMeta.displayName) : cVal;
            const sName = sMeta ? getOptionDisplayName(sMeta.displayName) : (sVal === 'SIZE_NA' ? '' : sVal);
            return (
              <div key={`entry:${k}`} className="dp-qty-row" onClick={() => goPreview(cVal, sVal)}>
                <span className="dp-color-swatch" style={{ background: bg }} title={cName} />
                <span style={{ fontWeight: 600 }}>{sName ? `${cName} · ${sName}` : cName}</span>
                <div className="dp-qty-controls">
                  <button className="dp-qty-btn" onClick={(e) => { e.stopPropagation(); decEntry(k); }}>−</button>
                  <input className="dp-qty-input" type="number" min={0} step={1} value={q}
                    onChange={(e) => {
                      const n = Math.max(0, parseInt(e.target.value || '0', 10) || 0);
                      if (n === 0) removeEntry(k); else setBatchQty(m => ({ ...m, [k]: n }));
                    }} />
                  <button className="dp-qty-btn" onClick={(e) => { e.stopPropagation(); incEntry(k); }}>+</button>
                  <button className="dp-qty-btn" onClick={(e) => { e.stopPropagation(); removeEntry(k); }}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Simple translator for Notes fields supporting {en, cn} objects
  const tr = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    try {
      const lang = getLanguageFromUrl();
      return val[lang] || val.en || val.cn || String(val);
    } catch {
      return String(val);
    }
  };

  const renderByDisplayMethod = (optKey, def) => {
    const method = (def?.displayMethod || '').toLowerCase();
    switch (method) {
      case 'horizontalbuttongroup':
        return renderHorizontalButtonGroup(optKey, def);
      case 'verticalradiobutton':
        return renderVerticalRadioButton(optKey, def);
      case 'colorswatchgrid':
        return renderColorSwatchGrid(optKey, def);
      case 'colorswatchgridqty':
        return batchEnabled ? renderColorSwatchGrid(optKey, def) : renderColorSwatchGridQty(optKey, def);
      default:
        // Fallback to horizontal buttons if not specified
        return renderHorizontalButtonGroup(optKey, def);
    }
  };

  if (!product) {
    return (
      <div className="error">
        <p>{t('product.noData', 'No product data available')}</p>
      </div>
    );
  }

  // Show only the base product name (no variant path). Prefer JSON ProductName.
  const rawName = product?.ProductionName || product?.name || '';
  const baseFromProduction = (rawName.includes('/') ? rawName.split('/')[0] : rawName).trim();
  const productTitle = configData?.SupportedModels?.ProductName || baseFromProduction;
  return (
    <div className="product-tab">
      <h1 className="product-name">{productTitle}</h1>
      <div className="selection-details">
        {optionsList
          .filter(([, def]) => Array.isArray(def?.values) && def.values.length > 1)
          .map(([optKey, def]) => (
            <div className="dp-section option-selection" key={optKey}>
              <div className="dp-section-title-row">
                <p className="option-title">
                  <strong>{getOptionDisplayName(def.displayName)}</strong>
                </p>
                <div className="dp-section-right">
                  {String(def?.displayMethod || '').toLowerCase() === 'colorswatchgridqty' ? (
                    (() => {
                      const totalCfg = def?.qty || def?.total_qty || {};
                      const sum = (totalCfg?.sum != null) ? Number(totalCfg.sum) : (totalCfg?.max != null ? Number(totalCfg.max) : null);
                      if (sum == null) return null;
                      const total = Object.values(qtyByColor || {}).reduce((s, n) => s + (Number(n) || 0), 0);
                      const color = total === sum ? '#2e7d32' : '#d32f2f';
                      return <span className="dp-qty-summary" style={{ color }}>{total} / {sum}</span>;
                    })()
                  ) : null}
                  {def?.Notes ? (
                    <button
                      type="button"
                      className="dp-note-link"
                      onClick={() => {
                        const label = typeof def.Notes === 'object' ? tr(def.Notes.label || 'Notes') : tr(def.Notes);
                        const content = typeof def.Notes === 'object' ? tr(def.Notes.content ?? '') : tr(def.Notes);
                        setNoteTitle(label);
                        setNoteBody(content);
                        setNoteOpen(true);
                      }}
                    >
                      {typeof def.Notes === 'object' ? tr(def.Notes.label || 'Notes') : tr(def.Notes)}
                    </button>
                  ) : null}
                </div>
              </div>
              {renderByDisplayMethod(optKey, def)}
            </div>
          ))}
      </div>
      <OptionNoteModal open={noteOpen} title={noteTitle} note={noteBody} onClose={() => setNoteOpen(false)} />
      {batchEnabled ? renderTempCart() : null}

      
    </div>
  );
};

export default DeliverPrint_ProductTab;
